import { useCallback, useEffect, useMemo, useState } from 'react'
import { convertPointsToPrice } from 'constants/merchandise'
import { useAppSelector } from 'hooks/reduxHooks'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import { selectLoggedIn } from 'selectors/accountSelectors'
import MerchandiseOfferCarousel from './MerchandiseOfferCarousel'
import { FormattedMessage } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import useMerchandiseCategoriesList from 'hooks/Merchandise/useMerchandiseCategoriesList'
import useMerchandiseCategoryProductCounts from 'hooks/Merchandise/useMerchandiseCategoryProductCounts'
import { getMerchandiseSearchUrl } from '../merchandiseUtils'
import {
  MAX_POINTS_CATEGORY_PILLS,
  MerchandisePointsCarouselCategoryPillsRow,
  MIN_PRODUCTS_FOR_POINTS_CATEGORY_PILL,
} from './MerchandisePointsCarouselCategoryPills'

const MAX_POINTS = 25000000
const MIN_POINTS = 2500
const PREMIUM_FLOOR_POINTS = 375000
const PREMIUM_THRESHOLD = 1000000

const POINTS_CATEGORY_CANDIDATE_LIMIT = 15

const BUFFER_TIERS = [
  { minPoints: 159300, buffer: 0.10 },
  { minPoints: 141600, buffer: 0.20 },
  { minPoints: 129000, buffer: 0.30 },
  { minPoints: 106200, buffer: 0.40 },
] as const

const DEFAULT_BUFFER = 0.50

const PARENT_CATEGORY_RANK_FILTERS: App.MerchandiseCategoryFilters = {
  sortBy: 'top',
  level: 0,
  limit: POINTS_CATEGORY_CANDIDATE_LIMIT,
}

function isPremiumUser(points: number): boolean {
  return points >= PREMIUM_THRESHOLD
}

function getBufferPercentage(points: number): number {
  const tier = BUFFER_TIERS.find(t => points >= t.minPoints)
  return tier?.buffer ?? DEFAULT_BUFFER
}

function calculatePriceRange(points: number): { min: number, max: number } {
  if (isPremiumUser(points)) {
    return {
      min: convertPointsToPrice(PREMIUM_FLOOR_POINTS),
      max: convertPointsToPrice(MAX_POINTS),
    }
  }

  const buffer = getBufferPercentage(points)
  const calculatedMin = Math.floor(points * (1 - buffer))
  const calculatedMax = Math.floor(points * (1 + buffer))

  const finalMax = Math.min(MAX_POINTS, calculatedMax)
  const finalMin = Math.max(MIN_POINTS, Math.min(calculatedMin, finalMax))

  return {
    min: convertPointsToPrice(finalMin),
    max: convertPointsToPrice(finalMax),
  }
}

interface Props {
  filters?: App.MerchandiseFilters
  trackingPageName: string
}

function MerchandisePointsCarousel(props: Props) {
  const { filters, trackingPageName } = props
  const points = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const isLoggedIn = useAppSelector(state => selectLoggedIn(state))

  const shouldShowCarousel = isLoggedIn && points >= MIN_POINTS

  const pointsFilter = useMemo((): App.MerchandiseFilters | undefined => {
    if (!shouldShowCarousel) {
      return
    }

    const priceRange = calculatePriceRange(points)

    return {
      ...(filters ?? {}),
      price: priceRange,
      sortBy: 'priceAsc',
    }
  }, [points, shouldShowCarousel, filters])

  const [wrapRef, wrapInView] = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })

  const [rankedParentCategoryIds, fetchingRankedCategoryIds] = useMerchandiseCategoriesList(
    PARENT_CATEGORY_RANK_FILTERS,
    { disabled: !shouldShowCarousel || !wrapInView },
  )

  const categoryProductCounts = useMerchandiseCategoryProductCounts(
    rankedParentCategoryIds,
    pointsFilter,
    { disabled: !wrapInView },
  )

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)

  const onSelectCategory = useCallback((categoryId: string | null) => {
    setSelectedCategoryId(categoryId)
  }, [])

  const allCountsSettled = useMemo(() => {
    if (rankedParentCategoryIds.length === 0) {
      return false
    }
    return rankedParentCategoryIds.every((id) => categoryProductCounts[id] !== undefined)
  }, [rankedParentCategoryIds, categoryProductCounts])

  const qualifyingCategoryIds = useMemo(() => {
    if (!allCountsSettled) {
      return []
    }
    return rankedParentCategoryIds
      .filter((id) => (categoryProductCounts[id] ?? 0) >= MIN_PRODUCTS_FOR_POINTS_CATEGORY_PILL)
      .slice(0, MAX_POINTS_CATEGORY_PILLS)
  }, [allCountsSettled, rankedParentCategoryIds, categoryProductCounts])

  useEffect(() => {
    if (selectedCategoryId && !qualifyingCategoryIds.includes(selectedCategoryId)) {
      setSelectedCategoryId(null)
    }
  }, [qualifyingCategoryIds, selectedCategoryId])

  const showCategoryPills = !fetchingRankedCategoryIds && qualifyingCategoryIds.length >= 2

  const carouselFilters = useMemo((): App.MerchandiseFilters | undefined => {
    if (!pointsFilter) {
      return
    }
    if (!selectedCategoryId) {
      return pointsFilter
    }
    return {
      ...pointsFilter,
      categories: [selectedCategoryId],
    }
  }, [pointsFilter, selectedCategoryId])

  const pointsUrl = useMemo(() => {
    if (!shouldShowCarousel || !pointsFilter) {
      return
    }

    return getMerchandiseSearchUrl({
      ...(filters ?? {}),
      price: pointsFilter.price,
      sortBy: 'priceAsc',
      ...(selectedCategoryId ? { categories: [selectedCategoryId] } : {}),
    })
  }, [pointsFilter, shouldShowCarousel, filters, selectedCategoryId])

  const categoryPillsRow = useMemo(() => {
    if (!showCategoryPills) {
      return
    }
    return (
      <MerchandisePointsCarouselCategoryPillsRow
        categoryIds={qualifyingCategoryIds}
        selectedCategoryId={selectedCategoryId}
        onSelectCategory={onSelectCategory}
      />
    )
  }, [onSelectCategory, qualifyingCategoryIds, selectedCategoryId, showCategoryPills])

  if (!shouldShowCarousel) {
    return null
  }

  return (
    <div ref={wrapRef}>
      <MerchandiseOfferCarousel
        title={
          <FormattedMessage
            id="x4IQrCI_L_Bkpn8KvB57"
            defaultMessage="Shop with your <em>{formattedPoints}</em>"
            description="Title for points-based merchandise carousel in the shop section"
            values={{
              formattedPoints: <FormatLoyaltyPoints points={points} abbreviated />,
              em: (chunks) => <em>{chunks}</em>,
            }}
          />
        }
        filters={carouselFilters}
        viewAllTo={pointsUrl}
        trackingListName="Shop with your points"
        trackingPageName={trackingPageName}
        externalInView={wrapInView}
        belowTitle={categoryPillsRow}
      />
    </div>
  )
}

export default MerchandisePointsCarousel
